import React, { useEffect, useMemo, useState } from "react"
import DynamicComponent from ".."
import DoubleHeading from "components/DoubleHeading"
import useWindowSize from "utilities/useWindowSize"
import CallToAction from "components/CallToAction"
import { debounce } from "lodash"
import PhotoGrid from "../molecules/PhotoGrid"
import CTABanner from "components/CTABanner"

export default function TeamPage({ story }) {
  const [teamMembers, setTeamMembers] = useState([])
  const [filterTeam, setFilterTeam] = useState("")
  const [search, setSearch] = useState("")
  const [loading, setLoading] = useState(false)

  const filteredTeamMembers = useMemo(
    () =>
      teamMembers
        ?.filter((member) => {
          if (filterTeam && !member?.content?.department?.includes(filterTeam)) return false
          if (
            search &&
            !member?.content?.name?.toLowerCase().includes(search.toLowerCase()) &&
            !member?.content?.title?.toLowerCase().includes(search.toLowerCase())
          )
            return false
          return true
        })
        ?.map((photo) => ({
          src: photo.content?.image?.filename,
          blurDataURL: photo.content?.image?.blurDataURL,
          uuid: photo.uuid,
          alt: `Photo of ${photo.content?.name}`,
          tooltipContent: (
            <>
              <span className="font-tertiary inline-block text-m3">Hi, I&apos;m</span>
              <span className="uppercase font-semibold text-m2"> {photo.content?.name}</span>
              <div className="text-base">{photo.content?.title}</div>
            </>
          ),
        })),
    [search, filterTeam, teamMembers],
  )

  useEffect(() => {
    setLoading(true)
  }, [search, filterTeam])

  useEffect(() => {
    setLoading(false)
  }, [filteredTeamMembers])

  const filterableTeams = useMemo(() => {
    const departments = teamMembers
      ?.flatMap((member) => member?.content?.department || []) // Flatten arrays of department strings
      .filter((dept) => dept) // Filter out null/undefined values
      .map((dept) => dept.trim()) // Trim team name just in case

    return [...new Set(departments)]
  }, [teamMembers])

  const handleSearch = debounce((e) => {
    setSearch(e.target.value)
  }, 200)

  const handleFilterTeam = (e) => {
    setFilterTeam(e.target.value)
  }

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const shuffledResults = story?.teamMembers?.sort(() => Math.random() - 0.5)
        const allTeamMembers = shuffledResults?.map((teamMember, index) => ({
          ...teamMember,
          size:
            index < Math.floor(shuffledResults.length * 0.04)
              ? "triple"
              : index < Math.floor(shuffledResults.length * 0.16)
                ? "double"
                : "single",
        }))
        setTeamMembers(allTeamMembers)
        setLoading(false)
      } catch (error) {
        console.error(error.message)
        setLoading(false)
      }
    }
    fetchTeam()
  }, [story.teamMembers])

  return (
    <main className="page-content">
      {story.page?.content?.blocks?.map((blok) => (
        <DynamicComponent blok={blok} key={blok._uid} />
      ))}
      <section className="bg-purple-gradient py-12 lg:pt-32 lg:pb-32 text-white">
        <div className="container mx-auto">
          <div className="max-w-screen-xl mx-auto">
            <div className="text-center mx-auto max-w-xl 2xl:max-w-3xl pb-8">
              {story.page?.content?.team_grid_eyebrow && (
                <span className="eyebrow eyebrow-2 text-green-kelly">{story.page?.content?.team_grid_eyebrow}</span>
              )}
              <DoubleHeading
                heading={story.page?.content?.team_grid_heading}
                heading2={story.page?.content?.team_grid_heading2}
              />
            </div>
            <div className="flex flex-col-reverse items-center justify-between md:flex-row  pb-10 font-secondary md:items-end">
              <div className="mb-3 md:mb-0">
                <label className="block" htmlFor="team">
                  Team
                </label>
                <select
                  value={filterTeam || ""}
                  onChange={handleFilterTeam}
                  className="bg-blue/0 text-white border border-white rounded-lg py-1 px-2 min-w-64 xs:min-w-72"
                  id="team"
                >
                  <option value="" className="text-charcoal">
                    All
                  </option>
                  {filterableTeams?.map((value, i) => {
                    return (
                      <option key={`option-${i}-${value}`} value={value} className="text-charcoal">
                        {value}
                      </option>
                    )
                  })}
                </select>
              </div>
              <div className="mb-3 md:mb-0">
                <label className="block" htmlFor="search">
                  Search
                </label>
                <input
                  onChange={handleSearch}
                  type="search"
                  id="search"
                  placeholder="Name or title"
                  className="bg-blue/0 text-white border border-white rounded-lg py-1 px-2 min-w-64 xs:min-w-72 max-font-secondary"
                />
              </div>
            </div>
            <PhotoGrid
              fallbackImage="/assets/blog-item-placeholder.svg"
              photos={filteredTeamMembers}
              loading={loading}
              mixSizes={!search && !filterTeam}
            />
            <div className=" mt-6 lg:mt-8 relative before:rounded-4xl before:top-0 before:left-0 rounded-4xl before:content-[' '] before:absolute before:w-full before:h-full before:bg-warm-white/70 before:mix-blend-overlay before:border-2 border-solid before:border-white p-6 text-center">
              <div className="uppercase tracking-wider font-secondary flex items-center gap-4 lg:gap-12 flex-col lg:flex-row justify-center">
                <div className="mt-1">Want to join an awesome team?</div>
                <CallToAction style="secondary-dark" href="/careers">
                  Careers @ 2060
                </CallToAction>
              </div>
            </div>
          </div>
        </div>
      </section>
      <CTABanner
        heading={story.page?.content?.cta_heading}
        asideText={story.page?.content?.cta_aside_text}
        buttonText={story.page?.content?.cta_button_text}
        buttonLink={story.page?.content?.cta_button_link}
        content={story.page?.content?.cta_content}
      />
    </main>
  )
}
